import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Recipient } from "src/types";

type TabKey = "canSend" | "noEmail" | "postOnly";

function CanSendRow({ recipient, checked, onToggle }: { recipient: Recipient; checked: boolean; onToggle: (id: string) => void }) {
    const { t } = useTranslation();
    const name = `${recipient.firstName ?? ""} ${recipient.lastName ?? ""}`.trim();
    const status = recipient.questionnaireStatus;
    return (
        <>
            <span className="bsp-grid__check">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(recipient.id)}
                    aria-label={name}
                />
            </span>
            <span className="bsp-grid__info">
                <span className="bsp-grid__info-name">{name}</span>
                <span className="bsp-grid__info-addr">{recipient.email}</span>
            </span>
            <span className="bsp-grid__cost-pages">
                {status === "submitted" && (
                    <span className="bsp-q-badge bsp-q-badge--submitted">{t("patients.bulkQuestionnaireSubmitted")}</span>
                )}
                {status === "opened" && (
                    <span className="bsp-q-badge bsp-q-badge--opened">{t("patients.bulkQuestionnaireOpened")}</span>
                )}
                {status === "sent" && (
                    <span className="bsp-q-badge bsp-q-badge--sent">{t("patients.bulkQuestionnaireAlreadySent")}</span>
                )}
            </span>
        </>
    );
}

function SkippedRow({ recipient, reason }: { recipient: Recipient; reason: string }) {
    const name = `${recipient.firstName ?? ""} ${recipient.lastName ?? ""}`.trim();
    return (
        <>
            <span className="bsp-grid__info">
                <span className="bsp-grid__info-name">{name}</span>
                {recipient.email
                    ? <span className="bsp-grid__info-addr">{recipient.email}</span>
                    : <span className="bsp-grid__info-addr bsp-grid__info-addr--missing">—</span>}
            </span>
            <span className="bsp-grid__cost-pages bsp-q-reason">{reason}</span>
        </>
    );
}

interface Props {
    canSend: Recipient[];
    noEmail: Recipient[];
    postOnly: Recipient[];
    selected: Set<string>;
    onToggle: (id: string) => void;
    onToggleAll: () => void;
}

export default function BulkQuestionnaireRecipientPreview({ canSend, noEmail, postOnly, selected, onToggle, onToggleAll }: Props) {
    const { t } = useTranslation();

    const tabs: { key: TabKey; label: string }[] = [
        canSend.length > 0  && { key: "canSend" as TabKey,  label: t("patients.bulkQuestionnaireCanSend",  { count: canSend.length }) },
        noEmail.length > 0  && { key: "noEmail" as TabKey,  label: t("patients.bulkQuestionnaireNoEmail",  { count: noEmail.length }) },
        postOnly.length > 0 && { key: "postOnly" as TabKey, label: t("patients.bulkQuestionnairePostOnly", { count: postOnly.length }) },
    ].filter(Boolean) as { key: TabKey; label: string }[];

    const [active, setActive] = useState<TabKey>(tabs[0]?.key ?? "canSend");

    const allChecked = canSend.length > 0 && canSend.every((r) => selected.has(r.id));

    return (
        <div className="bsp bsp--questionnaire">
            <div className="bsp__tabs" role="tablist">
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={active === tab.key}
                        className={`bsp__tab bsp__tab--${tab.key}${active === tab.key ? " bsp__tab--active" : ""}`}
                        onClick={() => setActive(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
            <div className="bsp__panel" role="tabpanel">
                {active === "canSend" && (
                    <div className="bsp-grid bsp-grid--questionnaire">
                        <span className="bsp-grid__hdr">
                            <input
                                type="checkbox"
                                checked={allChecked}
                                onChange={onToggleAll}
                                aria-label={t("patients.bulkQuestionnaireSelectAll")}
                            />
                        </span>
                        <span className="bsp-grid__hdr">{t("table.recipient")}</span>
                        <span className="bsp-grid__hdr bsp-grid__hdr--r">{t("table.status")}</span>
                        {canSend.map((r) => (
                            <CanSendRow key={r.id} recipient={r} checked={selected.has(r.id)} onToggle={onToggle} />
                        ))}
                    </div>
                )}
                {active === "noEmail" && (
                    <div className="bsp-grid bsp-grid--questionnaire-no-email">
                        <span className="bsp-grid__hdr">{t("table.recipient")}</span>
                        <span className="bsp-grid__hdr bsp-grid__hdr--r">{t("patients.bulkQuestionnaireReason")}</span>
                        {noEmail.map((r) => <SkippedRow key={r.id} recipient={r} reason={t("patients.bulkQuestionnaireNoEmailReason")} />)}
                    </div>
                )}
                {active === "postOnly" && (
                    <div className="bsp-grid bsp-grid--questionnaire-no-email">
                        <span className="bsp-grid__hdr">{t("table.recipient")}</span>
                        <span className="bsp-grid__hdr bsp-grid__hdr--r">{t("patients.bulkQuestionnaireReason")}</span>
                        {postOnly.map((r) => <SkippedRow key={r.id} recipient={r} reason={t("patients.bulkQuestionnairePostOnlyReason")} />)}
                    </div>
                )}
            </div>
        </div>
    );
}
